import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import IntelCard from '../components/IntelCard';
import RivalryCard from '../components/RivalryCard';
import EvolutionTimeline from '../components/EvolutionTimeline';
import ArchetypeCard from '../components/ArchetypeCard';
import Icon from '../components/Icon';
import LiveDraftExperience from '../components/LiveDraftExperience';

const GRADE_COLORS = {
  'A+': '#00e676', 'A': '#00e676', 'A-': '#52d98a',
  'B+': 'var(--accent)', 'B': 'var(--accent)', 'B-': 'var(--accent)',
  'C+': '#ffd32a', 'C': '#ffd32a', 'C-': '#e8c244',
  'D+': 'var(--accent2)', 'D': 'var(--accent2)', 'F': '#ff4757',
};
const POS_COLORS = { QB: 'var(--accent2)', RB: '#00e676', WR: 'var(--accent)', TE: '#ffd32a' };
const DIRECTION_COLORS = { Contender: '#00e676', Rebuilding: 'var(--accent)', Neutral: '#ffd32a' };
const PHASE_COLORS = { early: '#00e676', midseason: 'var(--accent)', deadline: '#ffd32a', playoffs: 'var(--accent2)', offseason: '#a855f7' };

const WEIGHT_CLASS_STYLES = {
  legendary: { color: '#ffd32a', bg: 'rgba(255,211,42,0.08)', border: 'rgba(255,211,42,0.25)', icon: '🏆' },
  major:     { color: 'var(--accent)', bg: 'rgba(var(--accent-rgb),0.08)',  border: 'rgba(var(--accent-rgb),0.25)',  icon: '⭐' },
  minor:     { color: '#00e676', bg: 'rgba(0,230,118,0.08)',  border: 'rgba(0,230,118,0.25)',  icon: '✅' },
  milestone: { color: '#a855f7', bg: 'rgba(168,85,247,0.08)', border: 'rgba(168,85,247,0.25)', icon: '🛡️' },
};

const ARCHETYPE_STYLES = {
  "The Dynasty Architect":   { color: '#ffd32a', icon: '🏛️', bg: 'rgba(255,211,42,0.08)',  border: 'rgba(255,211,42,0.25)' },
  "The Win-Now Mercenary":   { color: 'var(--accent2)', icon: '⚔️', bg: 'rgba(var(--accent2-rgb),0.08)',  border: 'rgba(var(--accent2-rgb),0.25)' },
  "The Deadline Raider":     { color: '#ff4757', icon: '⏰', bg: 'rgba(255,71,87,0.08)',   border: 'rgba(255,71,87,0.25)' },
  "The Trade Addict":        { color: '#a855f7', icon: '🔄', bg: 'rgba(168,85,247,0.08)',  border: 'rgba(168,85,247,0.25)' },
  "The Reluctant Rebuilder": { color: '#888888', icon: '🔧', bg: 'rgba(136,136,136,0.08)', border: 'rgba(136,136,136,0.25)' },
  "The Sleeper Cell":        { color: 'var(--accent)', icon: '🥷', bg: 'rgba(var(--accent-rgb),0.08)',   border: 'rgba(var(--accent-rgb),0.25)' },
  "The Draft Evangelist":    { color: '#00e676', icon: '📋', bg: 'rgba(0,230,118,0.08)',   border: 'rgba(0,230,118,0.25)' },
  "The Chaos Agent":         { color: '#e040fb', icon: '🌪️', bg: 'rgba(224,64,251,0.08)',  border: 'rgba(224,64,251,0.25)' },
  "The Ironclad":            { color: '#4dc8e8', icon: '🛡️', bg: 'rgba(77,200,232,0.08)',  border: 'rgba(77,200,232,0.25)' },
  "The Legacy Builder":      { color: '#c8a200', icon: '👑', bg: 'rgba(200,162,0,0.08)',   border: 'rgba(200,162,0,0.25)' },
};

const DOMAIN_LABELS = {
  transaction_aggression: { label: 'Transaction Aggression', desc: 'Trade & waiver activity' },
  asset_philosophy:       { label: 'Asset Philosophy',       desc: 'Pick accumulation & youth bias' },
  contention_behavior:    { label: 'Contention Behavior',    desc: 'Win-now aggression' },
  roster_construction:    { label: 'Roster Construction',    desc: 'Positional concentration' },
};

function GradeBox({ grade, size = 32 }) {
  const color = GRADE_COLORS[grade] || '#888';
  return (
    <div style={{
      width: size, height: size, borderRadius: 6,
      background: color + '22', color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-display)', fontSize: size * 0.34,
      border: `1px solid ${color}44`, flexShrink: 0,
    }}>{grade || '?'}</div>
  );
}

function StatBox({ label, value, color, sub }) {
  return (
    <div style={{
      background: 'var(--bg3)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '12px 14px', textAlign: 'center', minWidth: 80,
    }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: color || 'var(--text)', lineHeight: 1 }}>{value ?? '—'}</div>
      <div style={{ fontSize: '0.6rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'var(--font-mono)', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.65rem', color: 'var(--text2)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function DomainBar({ label, desc, score }) {
  const pct = Math.min(100, (score / 10) * 100);
  const color = score >= 7 ? 'var(--green)' : score >= 4 ? 'var(--accent)' : '#ffd32a';
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div>
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text)' }}>{label}</span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text3)', marginLeft: 8 }}>{desc}</span>
        </div>
        <span style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono)', color, fontWeight: 700 }}>{score}/10</span>
      </div>
      <div style={{ height: 6, background: 'var(--bg4)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: `linear-gradient(90deg, ${color}88, ${color})`,
          borderRadius: 3, transition: 'width 0.8s ease',
        }} />
      </div>
    </div>
  );
}

function TendencyProfileCard({ tendency }) {
  if (!tendency || tendency.status === 'building') {
    return (
      <div style={{
        background: 'rgba(136,136,136,0.06)', border: '1px solid rgba(136,136,136,0.2)',
        borderRadius: 12, padding: '20px 24px', marginBottom: 20,
      }}>
        <div style={{ fontSize: '0.65rem', color: 'var(--text3)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>
          Behavioral Profile
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--text2)' }}>
          Tendency Profile Building...
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text3)', marginTop: 8 }}>
          More seasons of data needed to establish a confident behavioral pattern.
        </div>
      </div>
    );
  }

  const archetype = tendency.archetype_primary;
  const style = ARCHETYPE_STYLES[archetype] || { color: 'var(--text3)', icon: '🎯', bg: 'rgba(136,136,136,0.08)', border: 'rgba(136,136,136,0.25)' };
  const confidence = tendency.confidence_score || 0;
  const confidenceLabel = confidence >= 0.7 ? 'Established' : confidence >= 0.5 ? 'Emerging' : 'Developing';
  const confidenceColor = confidence >= 0.7 ? 'var(--green)' : confidence >= 0.5 ? '#ffd32a' : '#888';
  const domains = tendency.domain_scores || {};

  return (
    <div style={{
      background: style.bg, border: `1px solid ${style.border}`,
      borderRadius: 12, padding: '20px 24px', marginBottom: 20,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${style.color}, transparent)` }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: '0.65rem', color: style.color, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 6, opacity: 0.8 }}>
            Behavioral Archetype
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon emoji={style.icon} size={28} />
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: style.color, letterSpacing: '1px', lineHeight: 1.1 }}>
              {archetype}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.62rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>CONFIDENCE</div>
          <div style={{
            fontSize: '0.72rem', padding: '3px 10px', borderRadius: 8,
            background: confidenceColor + '18', color: confidenceColor,
            border: `1px solid ${confidenceColor}33`, fontFamily: 'var(--font-mono)', fontWeight: 700,
          }}>
            {confidenceLabel} · {Math.round(confidence * 100)}%
          </div>
        </div>
      </div>

      {tendency.tendency_tags?.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {tendency.tendency_tags.map((tag, i) => (
            <span key={i} style={{
              fontSize: '0.68rem', padding: '3px 10px', borderRadius: 6,
              background: 'rgba(0,0,0,0.3)', color: 'var(--text2)',
              border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'var(--font-mono)',
            }}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {tendency.narrative_summary && !tendency.narrative_summary.includes('shows a consistent behavioral pattern as') && (
        <div style={{
          fontSize: '0.82rem', color: 'var(--text2)', lineHeight: 1.6,
          padding: '12px 14px', background: 'rgba(0,0,0,0.2)', borderRadius: 8, marginBottom: 16,
          fontStyle: 'italic',
        }}>
          "{tendency.narrative_summary}"
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: '0.62rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.5px', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>
          Domain Profile
        </div>
        {Object.entries(domains).map(([key, score]) => {
          const meta = DOMAIN_LABELS[key] || { label: key, desc: '' };
          return <DomainBar key={key} label={meta.label} desc={meta.desc} score={score} />;
        })}
      </div>
    </div>
  );
}

function AchievementBadge({ achievement }) {
  const style = WEIGHT_CLASS_STYLES[achievement.weight_class] || WEIGHT_CLASS_STYLES.milestone;
  return (
    <div style={{
      background: style.bg, border: `1px solid ${style.border}`,
      borderRadius: 10, padding: '12px 16px',
      display: 'flex', alignItems: 'flex-start', gap: 12,
      position: 'relative', overflow: 'hidden',
    }}>
      {achievement.weight_class === 'legendary' && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${style.color}, transparent)` }} />
      )}
      <Icon emoji={style.icon} size={28} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', color: style.color, letterSpacing: '0.5px', lineHeight: 1.2, marginBottom: 4 }}>
          {achievement.label}
        </div>
        {achievement.description && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text2)', lineHeight: 1.4 }}>{achievement.description}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {achievement.season && (
            <span style={{ fontSize: '0.62rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', background: 'var(--bg4)', padding: '2px 6px', borderRadius: 4 }}>
              {achievement.season} Season
            </span>
          )}
          <span style={{ fontSize: '0.62rem', color: style.color, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {achievement.weight_class}
          </span>
          {achievement.is_manual && (
            <span style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>· Canon</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper — render the markdown-ish **bold** segments from verdict copy
function VerdictText({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <span>
      {parts.map((p, i) => p.startsWith('**') && p.endsWith('**')
        ? <strong key={i} style={{ color: 'var(--text)' }}>{p.slice(2, -2)}</strong>
        : <span key={i}>{p}</span>)}
    </span>
  );
}

// Pill for a player or pick — uses `label` from the unified grader
// (which already includes "(became X)" for resolved picks); falls back
// to the legacy `name` / "season R{round}" formats.
function AssetPill({ asset, kind, sign }) {
  const isReceived = sign === '+';
  const color = isReceived ? 'var(--green)' : 'var(--red)';
  const bg = isReceived ? 'rgba(0,230,118,0.08)' : 'rgba(255,71,87,0.08)';
  const label = asset.label
    || asset.name
    || (asset.season && asset.round ? `${asset.season} R${asset.round}` : '?');
  const value = typeof asset.value === 'number' ? asset.value : null;
  return (
    <span
      title={value !== null ? `Value: ${value.toFixed(0)} pts` : ''}
      style={{
        fontSize: '0.72rem', color, background: bg,
        padding: '2px 8px', borderRadius: 4,
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}
    >
      {sign}{label}
    </span>
  );
}

function TradeCard({ trade }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!trade) return <div style={{ fontSize: '0.78rem', color: 'var(--text3)', padding: '10px' }}>No data yet</div>;
  const grade = trade.grade || {};
  const color = GRADE_COLORS[grade.letter] || '#888';
  const atTime = trade.at_time || {};
  const aged = trade.aged || {};
  const isMultiTeam = trade.is_multi_team;
  const rationale = trade.rationale || [];
  const verdict = trade.verdict;

  // Pick out resolved-player picks for the drill-down
  const resolvedPicks = [
    ...((atTime?.received?.picks) || []),
    ...((atTime?.sent?.picks) || []),
  ].filter((p) => p.resolved_player_name);

  return (
    <div style={{ background: 'var(--bg3)', border: `1px solid ${color}33`, borderRadius: 8, padding: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0, flex: 1 }}>
          <GradeBox grade={grade.letter} size={28} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
              {trade.season} · {trade.phase} · vs {(trade.partners || []).join(', ')}
              {isMultiTeam && (
                <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: 'rgba(168,85,247,0.12)', color: '#c084fc', fontSize: '0.6rem', letterSpacing: 1 }}>
                  3-WAY
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.65rem', color }}>
              {atTime.received_value?.toFixed(0) ?? grade.value_in?.toFixed(0)} received · {atTime.sent_value?.toFixed(0) ?? grade.value_out?.toFixed(0)} sent
              {aged.shift !== undefined && Math.abs(aged.shift) > 0.5 && (
                <span style={{ marginLeft: 8, color: aged.direction === 'improved' ? 'var(--green)' : aged.direction === 'declined' ? 'var(--red)' : 'var(--text3)' }}>
                  · aged {aged.emoji} {aged.shift > 0 ? '+' : ''}{aged.shift?.toFixed(0)}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded((x) => !x); }}
          style={{
            background: 'transparent', border: '1px solid var(--border)',
            color: 'var(--text2)', padding: '4px 8px', borderRadius: 4,
            cursor: 'pointer', fontSize: '0.65rem', fontFamily: 'var(--font-mono)',
            letterSpacing: 1, flexShrink: 0,
          }}
        >
          {expanded ? '▲ HIDE' : '▼ DETAILS'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {(trade.players_in || []).map((p, i) => <AssetPill key={`pi${i}`} asset={p} kind="player" sign="+" />)}
        {(trade.picks_in || []).map((p, i) => <AssetPill key={`pki${i}`} asset={p} kind="pick" sign="+" />)}
        {(trade.players_out || []).map((p, i) => <AssetPill key={`po${i}`} asset={p} kind="player" sign="-" />)}
        {(trade.picks_out || []).map((p, i) => <AssetPill key={`pko${i}`} asset={p} kind="pick" sign="-" />)}
      </div>

      {expanded && (
        <div style={{
          marginTop: 12, paddingTop: 12,
          borderTop: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {/* AI verdict */}
          {verdict && (
            <div style={{
              fontSize: '0.82rem', lineHeight: 1.55,
              color: 'var(--text2)', fontStyle: 'italic',
              padding: '8px 10px', background: 'var(--bg4)', borderRadius: 6,
            }}>
              <VerdictText text={verdict} />
            </div>
          )}

          {/* At-time vs aged scores */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 4 }}>
                AT TIME
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text)' }}>{atTime.letter || grade.letter}</span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                  {atTime.received_value?.toFixed(0)} / {atTime.sent_value?.toFixed(0)}
                </span>
              </div>
            </div>
            <div style={{ background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 4 }}>
                AGED {aged.emoji}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '0.78rem', color: aged.direction === 'improved' ? 'var(--green)' : aged.direction === 'declined' ? 'var(--red)' : 'var(--text)' }}>
                  {aged.letter || '—'}
                </span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                  {aged.received_value?.toFixed(0)} / {aged.sent_value?.toFixed(0)}
                </span>
              </div>
            </div>
          </div>

          {/* Pick resolutions */}
          {resolvedPicks.length > 0 && (
            <div>
              <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6 }}>
                Pick Resolution
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {resolvedPicks.map((p, i) => (
                  <div key={i} style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>
                    <span style={{ color: 'var(--accent)' }}>{p.season} R{p.round}</span>
                    {' → '}
                    <span style={{ color: 'var(--text)' }}>{p.resolved_player_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rationale bullets */}
          {rationale.length > 0 && (
            <div>
              <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6 }}>
                Reasoning
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.74rem', color: 'var(--text2)', lineHeight: 1.5 }}>
                {rationale.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GradeDistBar({ dist }) {
  const order = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'F'];
  const total = Object.values(dist || {}).reduce((a, b) => a + b, 0) || 1;
  return (
    <div>
      <div style={{ display: 'flex', height: 20, borderRadius: 6, overflow: 'hidden', gap: 1 }}>
        {order.map(g => {
          const count = (dist || {})[g] || 0;
          if (!count) return null;
          const pct = (count / total) * 100;
          return (
            <div key={g} style={{ width: `${pct}%`, background: GRADE_COLORS[g] || '#888', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {pct > 8 && <span style={{ fontSize: '0.55rem', color: '#000', fontWeight: 700 }}>{g}</span>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        {order.filter(g => (dist || {})[g]).map(g => (
          <span key={g} style={{ fontSize: '0.62rem', color: GRADE_COLORS[g] || '#888', fontFamily: 'var(--font-mono)' }}>
            {g}: {dist[g]}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function MyIntel({ api }) {
  const { currentUser } = useUser();
  const [data, setData] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [tendency, setTendency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [intelCards, setIntelCards] = useState([]);
  const [draftCards, setDraftCards] = useState([]);
  const [draftTendencies, setDraftTendencies] = useState(null);
  const [draftAnalysis, setDraftAnalysis] = useState(null);
  const [draftNarrative, setDraftNarrative] = useState(null);
  const [draftRecap, setDraftRecap] = useState(null);

  const ownerId = currentUser?.owner_id || currentUser?.user_id;

  useEffect(() => {
    if (!ownerId || currentUser?.guest) return;
    const load = async () => {
      try {
        const [intelRes, achieveRes, tendencyRes] = await Promise.all([
          fetch(`${api}/api/manager/intel/${ownerId}`),
          fetch(`${api}/api/achievements?franchise_id=${ownerId}`),
          fetch(`${api}/api/manager/tendencies/${ownerId}`),
        ]);
        if (!intelRes.ok) throw new Error('Failed');
        setData(await intelRes.json());
        if (achieveRes.ok) {
          const achieveData = await achieveRes.json();
          setAchievements(achieveData.achievements || []);
        }
        if (tendencyRes.ok) setTendency(await tendencyRes.json());
        // Intel cards — load independently so failures don't break core page
        try {
          const icRes = await fetch(`${api}/api/intel-cards/${ownerId}`);
          if (icRes.ok) setIntelCards(await icRes.json());
        } catch(e) { console.log('Intel cards failed:', e); }
        try {
          const dcRes = await fetch(`${api}/api/draft/intel-cards/${ownerId}`);
          if (dcRes.ok) setDraftCards(await dcRes.json());
        } catch(e) { console.log('Draft cards failed:', e); }
        try {
          const dtRes = await fetch(`${api}/api/draft/tendencies/${ownerId}`);
          if (dtRes.ok) setDraftTendencies(await dtRes.json());
        } catch(e) { console.log('Draft tendencies failed:', e); }
        try {
          const daRes = await fetch(`${api}/api/draft/2026/analysis`);
          if (daRes.ok) setDraftAnalysis(await daRes.json());
        } catch(e) { console.log('Draft analysis failed:', e); }
        try {
          const dnRes = await fetch(`${api}/api/draft/narrative/${ownerId}`);
          if (dnRes.ok) { const dn = await dnRes.json(); setDraftNarrative(dn.narrative); }
        } catch(e) { console.log('Draft narrative failed:', e); }
        try {
          const drRes = await fetch(`${api}/api/draft/recap`);
          if (drRes.ok) { const dr = await drRes.json(); if (dr.status === 'complete') setDraftRecap(dr); }
        } catch(e) { console.log('Draft recap failed:', e); }
      } catch (e) {
        setError('Could not load your intel. Try again shortly.');
      }
      setLoading(false);
    };
    load();
  }, [api, ownerId]);

  if (currentUser?.guest || !ownerId) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <Icon emoji="👤" size={32} />
        <div style={{ color: 'var(--text3)', fontSize: '0.9rem' }}>Log in with your Sleeper username to see your personal intel.</div>
      </div>
    );
  }

  if (loading) return <div className="loading"><div className="spinner" /><span>Analyzing your 5-season history...</span></div>;
  if (error) return <div className="empty-state"><p>{error}</p></div>;
  if (!data) return null;

  const dirColor = DIRECTION_COLORS[data.direction] || '#888';

  const tabs = [
    { key: 'profile',      label: 'Tendency Profile', icon: '🎯' },
    { key: 'evolution',    label: 'Evolution',         icon: '🌀' },
    { key: 'rivalries',    label: 'Rivalries',         icon: '⚔️' },
    { key: 'achievements', label: 'Achievements',      icon: '🏆' },
    { key: 'trades',       label: 'Trade Profile',     icon: '🔄' },
    { key: 'waivers',      label: 'Waivers',           icon: '📋' },
    { key: 'draft',        label: 'Draft',             icon: '📦' },
  ];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">My Intel</div>
        <div className="page-subtitle">Your personal 5-season analytics · Behavioral profile · Trade grades</div>
      </div>

      {/* Identity banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(var(--accent-rgb),0.08), rgba(var(--accent2-rgb),0.06))',
        border: '1px solid rgba(var(--accent-rgb),0.2)', borderRadius: 12, padding: '20px 24px', marginBottom: 20,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--accent), var(--accent2))' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.65rem', color: 'rgba(var(--accent-rgb),0.8)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>Manager Profile</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', letterSpacing: '1px', color: 'var(--text)', lineHeight: 1.1 }}>{data.display_name}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: 'auto', alignItems: 'center' }}>
            <span style={{
              fontSize: '0.72rem', padding: '4px 12px', borderRadius: 20,
              background: dirColor + '18', color: dirColor, border: `1px solid ${dirColor}33`,
              fontFamily: 'var(--font-mono)', fontWeight: 700,
            }}>{data.direction}</span>
            {data.current_grade && <GradeBox grade={data.current_grade} size={40} />}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'All-Time Record', value: `${data.all_time_wins}-${data.all_time_losses}` },
            { label: 'Win %', value: `${(data.all_time_win_pct * 100).toFixed(0)}%`, color: data.all_time_win_pct > 0.5 ? 'var(--green)' : 'var(--red)' },
            { label: 'Championships', value: data.championships, color: data.championships > 0 ? 'var(--yellow)' : undefined },
            { label: 'Seasons', value: data.seasons },
          ].map((s, i) => (
            <div key={i}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: s.color || 'var(--text)', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '1px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Intel Cards */}
      {(intelCards.length > 0 || draftCards.length > 0) && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: '0.65rem', color: 'rgba(var(--accent-rgb),0.8)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Your Intel</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {intelCards.map((card, i) => (
              <IntelCard key={i} {...card} />
            ))}
            {draftCards.map((card, i) => (
              <IntelCard key={`draft-${i}`} {...card} />
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tab-bar tab-bar--scroll">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`tab-btn${activeTab === t.key ? ' active' : ''}`}>
            <Icon emoji={t.icon} size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && (
        <>
          <ArchetypeCard userId={ownerId} apiBase={api} />
          <div style={{ marginTop: 16 }}>
            <TendencyProfileCard tendency={tendency} />
          </div>
        </>
      )}

      {activeTab === 'evolution' && <EvolutionTimeline userId={ownerId} apiBase={api} />}

      {activeTab === 'rivalries' && <RivalryCard userId={ownerId} apiBase={api} />}

      {activeTab === 'achievements' && (
        <div>
          {achievements.length === 0 ? (
            <div className="empty-state"><p>No achievements yet.</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {achievements.map(a => <AchievementBadge key={a.id} achievement={a} />)}
            </div>
          )}
        </div>
      )}

      {activeTab === 'trades' && (
        <div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatBox label="Total Trades" value={data.total_trades} />
            <StatBox label="Avg Grade" value={data.avg_trade_grade ?? '—'} color="var(--accent)" />
            <StatBox label="Picks In" value={data.picks_acquired} color="var(--green)" />
            <StatBox label="Picks Out" value={data.picks_sent} color="var(--red)" />
            <StatBox label="Avg Age In" value={data.avg_age_acquired} />
            <StatBox label="Avg Age Out" value={data.avg_age_sent} />
            <StatBox label="Win-Now" value={`${data.win_now_score}/10`} color="var(--green)" />
            <StatBox label="Rebuild" value={`${data.rebuild_score}/10`} color="var(--accent)" />
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📊" size={13} />Trade Grade Distribution</div>
            <GradeDistBar dist={data.grade_distribution} />
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📅" size={13} />When Do You Trade?</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(data.trades_by_phase || {}).sort((a, b) => b[1] - a[1]).map(([phase, count]) => (
                <div key={phase} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px' }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: PHASE_COLORS[phase] || '#888' }} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text2)', textTransform: 'capitalize' }}>{phase}</span>
                  <span style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono)', color: PHASE_COLORS[phase] || '#888', fontWeight: 700 }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="🤝" size={13} />Most Frequent Trade Partners</div>
            {(data.trade_partners || []).map(([name, count], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < data.trade_partners.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 80, height: 4, background: 'var(--bg4)', borderRadius: 2 }}>
                    <div style={{ height: '100%', width: `${Math.min(100, count / (data.total_trades || 1) * 300)}%`, background: 'var(--accent)', borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--accent)', fontFamily: 'var(--font-mono)', minWidth: 55 }}>{count} trade{count !== 1 ? 's' : ''}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📍" size={13} />What Positions Do You Target?</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(data.pos_acquired || {}).sort((a, b) => b[1] - a[1]).filter(([pos]) => ['QB','RB','WR','TE'].includes(pos)).map(([pos, count]) => (
                <div key={pos} style={{ background: (POS_COLORS[pos] || '#888') + '15', border: `1px solid ${(POS_COLORS[pos] || '#888')}33`, borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: POS_COLORS[pos] || '#888', lineHeight: 1 }}>{count}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{pos}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="✅" size={13} />Best Trade</div>
              <TradeCard trade={data.best_trade} />
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="❌" size={13} />Worst Trade</div>
              <TradeCard trade={data.worst_trade} />
            </div>
          </div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="🕐" size={13} />Recent Trades</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(data.recent_trades || []).map((t, i) => <TradeCard key={i} trade={t} />)}
              {(!data.recent_trades || data.recent_trades.length === 0) && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text3)' }}>No trades recorded yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'draft' && (
        <div>
          {/* Draft archetype */}
          {draftTendencies && draftTendencies.status === 'ok' && (
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--accent)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>Draft Archetype</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: '1.8rem' }}>{draftTendencies.archetype?.emoji}</span>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: draftTendencies.archetype?.color || 'var(--accent)' }}>{draftTendencies.archetype?.name}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text2)', fontStyle: 'italic' }}>"{draftTendencies.archetype?.tagline}"</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { label: 'Career Picks', value: draftTendencies.total_picks },
                  { label: 'Avg / Season', value: draftTendencies.avg_picks_per_season },
                  { label: 'R1 Rate', value: `${Math.round((draftTendencies.r1_rate || 0) * 100)}%` },
                  { label: 'Top Position', value: draftTendencies.top_position },
                ].map((s, i) => (
                  <div key={i} style={{ background: 'var(--bg4)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--text)', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 3 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Draft DNA narrative */}
          {draftNarrative && (
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--accent)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>Draft DNA</div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text2)', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>"{draftNarrative}"</p>
            </div>
          )}

          <LiveDraftExperience
            api={api}
            picks={draftAnalysis?.analysis?.picks || []}
            currentUser={currentUser}
            draftComplete={Boolean(draftRecap)}
          />

          {/* Position breakdown */}
          {draftTendencies?.position_breakdown && (
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--accent)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Career Position Breakdown</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(draftTendencies.position_breakdown).sort((a,b) => b[1]-a[1]).map(([pos, count]) => {
                  const posColors = { QB: 'var(--accent2)', RB: '#00e676', WR: 'var(--accent)', TE: '#ffd32a' };
                  const color = posColors[pos] || '#888';
                  return (
                    <div key={pos} style={{ background: color + '15', border: `1px solid ${color}33`, borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color, lineHeight: 1 }}>{count}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{pos}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent picks */}
          {draftTendencies?.recent_picks?.length > 0 && (
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--accent)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Recent Picks</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[...draftTendencies.recent_picks].reverse().map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg4)', borderRadius: 8 }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', minWidth: 60 }}>{p.season} R{p.round}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text)' }}>{p.player}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text3)' }}>{p.position} · Pick #{p.pick_no}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}


      {/* Draft recap */}
      {draftRecap && activeTab === 'draft' && (() => {
        const myRecap = draftRecap.managers && draftRecap.managers.find(m => m.owner_id === ownerId);
        const gradeColor = (g) => {
          if (!g) return 'var(--text)';
          if (g.startsWith('A')) return 'var(--green)';
          if (g.startsWith('B')) return 'var(--accent)';
          if (g.startsWith('C')) return 'var(--yellow)';
          return 'var(--red)';
        };
        return (
          <div style={{ marginTop: 16 }}>
            {myRecap && (
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--accent)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Your 2026 Draft Grade</div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: gradeColor(myRecap.grade), lineHeight: 1, minWidth: 60 }}>{myRecap.grade}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text2)', lineHeight: 1.6, fontStyle: 'italic' }}>{myRecap.summary}</div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                  {[
                    { label: 'Picks', value: myRecap.picks ? myRecap.picks.length : 0 },
                    { label: 'Reaches', value: myRecap.reaches },
                    { label: 'Values', value: myRecap.values },
                    { label: 'Delta', value: (myRecap.total_delta >= 0 ? '+' : '') + myRecap.total_delta },
                  ].map((s, i) => (
                    <div key={i} style={{ background: 'var(--bg4)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--text)', lineHeight: 1 }}>{s.value}</div>
                      <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {myRecap.should_have && myRecap.should_have.length > 0 && (
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--red)', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>What you should have done</div>
                    {myRecap.should_have.map((s, i) => (
                      <div key={i} style={{ fontSize: '0.78rem', color: 'var(--text2)', marginBottom: 6 }}>
                        Pick {s.pick_no}: Took <span style={{ color: 'var(--red)' }}>{s.took}</span> (#{s.took_rank}) — <span style={{ color: 'var(--green)' }}>{s.should_have}</span> (#{s.should_rank}) was still available
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--accent)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>League Draft Rankings</div>
              {draftRecap.managers && draftRecap.managers.map((m, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < draftRecap.managers.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: gradeColor(m.grade), minWidth: 36 }}>{m.grade}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, color: m.owner_id === ownerId ? 'var(--accent)' : 'var(--text)' }}>{m.manager}{m.owner_id === ownerId ? ' (you)' : ''}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.summary}</div>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', textAlign: 'right', flexShrink: 0 }}>
                    <div>{m.picks ? m.picks.length : 0}pk</div>
                    <div style={{ color: m.total_delta <= 0 ? 'var(--green)' : 'var(--red)' }}>{m.total_delta >= 0 ? '+' : ''}{m.total_delta}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {activeTab === 'waivers' && (
        <div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatBox label="Waiver Claims" value={data.total_waiver_adds} />
            <StatBox label="FA Adds" value={data.total_fa_adds} />
            <StatBox label="Total Drops" value={data.total_drops} />
            <StatBox label="FAAB Spent" value={`$${data.total_faab_spent}`} color="var(--yellow)" />
            <StatBox label="Avg FAAB" value={`$${data.avg_faab_per_claim}`} />
            <StatBox label="Churn Rate" value={`${data.churn_rate}%`} color={data.churn_rate > 50 ? 'var(--red)' : data.churn_rate > 30 ? 'var(--yellow)' : 'var(--green)'} />
          </div>
          {Object.keys(data.faab_by_season || {}).length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="💰" size={13} />FAAB Spent by Season</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {Object.entries(data.faab_by_season || {}).sort().map(([season, spent]) => (
                  <div key={season} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--yellow)', lineHeight: 1 }}>${spent}</div>
                    <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{season}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="card">
            <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📍" size={13} />Waiver Adds by Position</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(data.pos_waiver_trends || {}).sort((a, b) => b[1] - a[1]).filter(([pos]) => ['QB','RB','WR','TE'].includes(pos)).map(([pos, count]) => (
                <div key={pos} style={{ background: (POS_COLORS[pos] || '#888') + '15', border: `1px solid ${(POS_COLORS[pos] || '#888')}33`, borderRadius: 8, padding: '10px 16px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: POS_COLORS[pos] || '#888', lineHeight: 1 }}>{count}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{pos}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
